import { useMemo } from 'react'
import classNames from 'classnames'
import { makeStyles } from 'transfix-ui/core/styles'
import { avatarColors } from 'transfix-ui'

export enum IssuePriority {
  P1 = 'p1',
  P2 = 'p2',
  P3 = 'p3',
  P4 = 'p4',
}

const useClasses = makeStyles({
  issueLabel: {
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: 15,
      height: 15,
      borderRadius: 3,
      marginRight: 6,
      marginBottom: 1,
    },
  },
  [IssuePriority.P1]: {
    '&::before': {
      backgroundColor: avatarColors.salmonRed,
    },
  },
  [IssuePriority.P2]: {
    '&::before': {
      backgroundColor: avatarColors.orange,
    },
  },
  [IssuePriority.P3]: {
    '&::before': {
      backgroundColor: avatarColors.iceBlue,
    },
  },
  [IssuePriority.P4]: {
    '&::before': {
      backgroundColor: avatarColors.steelBlue,
    },
  },
})

export const priorityOptions = [
  {
    label: 'P1 - High',
    short: 'P1',
    value: 1,
    type: IssuePriority.P1,
    description:
      'Critical Issue. All processes are halted and no workaround is currently available',
    notify: '1/2 hour',
    escalate: '1 hour',
  },
  {
    label: 'P2 - Medium',
    short: 'P2',
    value: 2,
    type: IssuePriority.P2,
    description: 'Non-critical problem. A workaround is available',
    notify: '1/2 hour',
    escalate: '2 hour',
  },
  {
    label: 'P3 - Low',
    short: 'P3',
    value: 3,
    type: IssuePriority.P3,
    description:
      'General incident and/or low system impact issue that does not require immediate attention.',
    notify: '6 hours',
    escalate: '24 hours',
  },
  {
    label: 'P4 - Very Low',
    short: 'P4',
    value: 4,
    type: IssuePriority.P4,
    description: 'Very Low.',
    notify: '24 hours',
    escalate: 'do not escalate',
  },
]

export const useIssuePriorityOptions = () => {
  const classes = useClasses()
  const memoPriorityOptions = useMemo(() => {
    return priorityOptions.map(({ type, ...issue }) => ({
      ...issue,
      className: classNames(classes.issueLabel, classes[type]),
    }))
  }, [classes])

  return memoPriorityOptions
}

export const useIssuePriorityLabel = (issuePriority?: number | null) => {
  const priority = priorityOptions.find(({ value }) => value === issuePriority)
  const classes = useClasses()

  if (!priority) {
    return {
      label: '',
      short: '',
      value: -1,
      className: classes.issueLabel,
      type: '',
      description: 'Priority not provided',
      notify: '',
      escalate: '',
    }
  }

  return {
    ...priority,
    className: classNames(classes.issueLabel, classes[priority.type]),
  }
}
